import React, { useState, useRef, useContext } from "react";
import {
  Box,
  TextField,
  Typography,
  Paper,
  IconButton,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";

import CommonAccordion from "../../custom-components/CommonAccordion";
import { ReactComponent as GenerateButton } from "../../../images/generateButton.svg";
import { ReactComponent as UserStoryIcon } from "../../../images/userStoryIcon.svg";
import { ReactComponent as DeleteIcon } from "../../../images/SecurityDeleteIcon.svg";
import {
  generateSastOwaspArtifacts,
  uploadFile,
} from "../../../services/webServices";
import { useNavigate } from "react-router-dom";
import {
  SecurityFlowDataContext,
  SecurityLoaderStateContext,
} from "../../main-pages/SecurityTestingPage";
import { LoadingModalContext } from "../../main-pages/ServicePage";
import MessagePopup from "../../popup-components/MessagePopup";
import SessionExpired from "../../popup-components/SessionExpiredPopup";

const ConstructionPhaseComponent = () => {
  const navigate = useNavigate();

  const { setLoadingState } = useContext(LoadingModalContext);
  const { setSecurityLoaderState } = useContext(SecurityLoaderStateContext);
  const { setConstructionPhase } = useContext(SecurityFlowDataContext);

  const [isFocused, setIsFocused] = useState(false);
  const [file, setFile] = useState(null);
  const [gitUrl, setGitUrl] = useState("");
  const [result, setResult] = useState([]);
  const [openErrorPopup, setOpenErrorPopup] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [selectRunType, setSelectRunType] = useState("");
  const [gitUrlError, setGitUrlError] = useState({ error: false, message: "" });

  const fileInputRef = useRef(null);

  const handleFileChange = async (event) => {
    const uploadedFile = event.target.files[0];

    if (uploadedFile) {
      const fileExtension = uploadedFile.name.split(".").pop().toLowerCase();

      if (fileExtension === "zip") {
        setFile(uploadedFile);
        setGitUrl("");
        setGitUrlError({ error: false, message: "" });
      } else {
        setErrorMessage(
          "Please upload a valid zip file or provide a valid repository URL."
        );
        setOpenErrorPopup(true); // Show error popup
        setFile(null);
        // alert("Only .zip files are allowed");
      }
    }
  };

  const handleRemoveFile = () => {
    setFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  function isValidGitRepoUrl(str) {
    if (!str || typeof str !== "string") return false;
    const re =
      /^(?:(?:https?|git|ssh|file):\/\/|(?:git@)[\w.-]+:)[\w.@:/\-~]+\.git\/?$/i;
    return re.test(str.trim());
  }

  // onChange
  const handleApiUrlChange = (event) => {
    const value = event.target.value;
    setGitUrl(value);
    setFile(null); // as in current code

    // Empty allowed? If required immediately, enforce; else only validate non-empty.
    // if (!value) {
    //   setGitUrlError({ error: true, message: "Repository URL is required." });
    //   return;
    // }

    if (!isValidGitRepoUrl(value)) {
      setGitUrlError({
        error: true,
        message:
          "Enter a valid Git repo URL (e.g., https://host/org/repo.git or git@host:org/repo.git).",
      });
    } else {
      setGitUrlError({ error: false, message: "" });
    }
  };

  const handleSASTScanClick = async () => {
    setSecurityLoaderState("Construction Phase");

    const formData = new FormData();
    if (file) {
      formData.append("file", file);
    }
    if (gitUrl) {
      formData.append("giturl", gitUrl);
    }
    let fake;

    const startProgress = (setProgress) => {
      let progress = 0;
      let stop = false;
      let finalMessageShown = false;

      const interval = setInterval(() => {
        if (stop) return;

        progress += 0.06;
        if (progress >= 97 && !finalMessageShown) {
          finalMessageShown = true;
          setLoadingState((prev) => ({
            ...prev,
            message: "Almost done! Fetching your output",
          }));
        }

        if (progress >= 97) progress = 97;

        setProgress((prev) => ({
          ...prev,
          progress: Math.max(prev.progress, Math.floor(progress)),
        }));
      }, 200);

      return {
        stop: () => {
          stop = true;
          clearInterval(interval);
        },
        completeTo100: async () => {
          stop = true;
          clearInterval(interval);

          // Rush to 100%
          setProgress((prev) => ({
            ...prev,
            progress: 100,
          }));
          await new Promise((res) => setTimeout(res, 500));
        },
      };
    };

    const finishTo100AndClose = async () => {
      await fake.completeTo100();
      setLoadingState({ open: false, message: "", progress: null });
    };
    // Initial message
    setLoadingState({
      open: true,
      message: "Running SAST Scan",
      progress: 0,
    });

    fake = startProgress(setLoadingState);

    try {
      // setLoading(true); // Start loading
      const response = await uploadFile(formData);

      if (!response.data.results || response.data.results.length === 0) {
        setErrorMessage(
          "Scan completed successfully. No issues were found in your code."
        );
        setOpenErrorPopup(true); // Show error message if no results
      }
      const SASTOWASPResponse = await generateSastOwaspArtifacts({
        sastResults: response.data.results,
      });
      setResult(SASTOWASPResponse.data.results);
      setConstructionPhase(SASTOWASPResponse.data.results);
      // console.log("SAST scan completed", response.data);
      // console.log("SAST OWASP LLM Response", SASTOWASPResponse.data?.results);
      await finishTo100AndClose();
      navigate("/dashboard/security-testing/construction-phase", {
        state: {
          runType: selectRunType,
          scanResults: SASTOWASPResponse.data?.results,
          // owaspResults: SASTOWASPResponse.data,
          date: response.data.date,
          codeSummary: response.data.codeMetrics,
        },
        replace: true,
      });
    } catch (error) {
      console.error("Error during SAST scan upload:", error);
      if (error.response && error.response.data) {
        if (error.response.data.Result === "Profanity Check Failed") {
          setOpenErrorPopup(true); // Show error popup
        }
      }
      if (error.response?.status === 401) {
        setSessionExpired(true);
      }
      setLoadingState({ open: false, message: "", progress: null });
      // Optionally show a user-friendly message
    } finally {
      // You can reset loading state or perform other final actions here
      // setLoading(false); // Stop loading
      setSecurityLoaderState(null); // Reset the loader state
      setLoadingState({ open: false, message: "", progress: null });
    }
  };

  const Details = (
    <Box sx={{ px: { xs: 1.5, sm: 0 } }}>
      {/* //Automation Testing Dropdown */}
      <Box mt={{ xs: 2, sm: 3 }}>
        <FormControl
          fullWidth
          variant="outlined"
          size="small"
          sx={{
            height: { xs: "52px", sm: "56px" },
            mb: { xs: 2, sm: 3 },
            width: "100%",
          }}
        >
          <InputLabel
            id="automation-test"
            shrink
            sx={{
              background: "white",
              px: 1,
              // keep label readable on small screens
              "&.MuiInputLabel-shrink": {
                fontSize: { xs: "0.9rem", sm: "1rem" },
              },
            }}
          >
            Select Run-1 or Run-2*
          </InputLabel>
          <Select
            labelId="run-type"
            value={selectRunType}
            onChange={(e) => setSelectRunType(e.target.value)}
            displayEmpty
            fullWidth
            sx={{
              height: { xs: "52px", sm: "56px" },
            }}
            renderValue={(selected) =>
              selected ? (
                selected
              ) : (
                <span style={{ color: "#999" }}>Select runtype</span>
              )
            }
          >
            <MenuItem disabled value="">
              <em>Select run type</em>
            </MenuItem>
            <MenuItem value="Run-1">Run-1</MenuItem>
            <MenuItem value="Run-2">Run-2</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* File Upload Section */}
      <Paper
        variant="outlined"
        sx={{
          pt: { xs: 2, sm: 3 },
          pb: { xs: 2, sm: 3 },
          px: { xs: 2, sm: 3 },
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: "center",
          justifyContent: "center",
          rowGap: { xs: 1, sm: 0 },
          border: "2px dashed #90caf9",
          cursor: file ? "default" : "pointer",
          mb: { xs: 3, sm: 4 },
          mt: { xs: 2, sm: 3 },
          backgroundColor: "#EAF4FF",
          textAlign: { xs: "center", sm: "left" },
        }}
      >
        <UserStoryIcon
          width={24}
          height={24}
          style={{ marginRight: 8, marginLeft: 3 }}
        />

        {file ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: { xs: "stretch", sm: "center" },
              justifyContent: "space-between",
              width: "100%",
              gap: { xs: 1, sm: 0 },
            }}
          >
            <Typography
              variant="body1"
              component="span"
              sx={{ color: "black", wordBreak: "break-word" }}
            >
              {file.name}
            </Typography>

            <Box sx={{ display: "flex", alignItems: "center", ml: { sm: 2 } }}>
              <Typography
                variant="body2"
                onClick={() => {
                  fileInputRef.current.value = "";
                  fileInputRef.current.click();
                }}
                sx={{
                  cursor: "pointer",
                  color: "#03A9F4",
                  mr: 2,
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                Change
              </Typography>
              <IconButton onClick={handleRemoveFile} size="small">
                <DeleteIcon sx={{ color: "red" }} />
              </IconButton>
            </Box>
          </Box>
        ) : (
          <label
            htmlFor="file-upload"
            style={{
              display: "flex",
              flexDirection: "column",
              cursor: "pointer",
            }}
          >
            <Typography
              variant="body1"
              component="span"
              sx={{
                color: "black",
                ":hover": { textDecoration: "underline" },
              }}
            >
              Upload Source Code
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Accepted formats: Zip file
            </Typography>
          </label>
        )}

        {/* Hidden File Input */}
        <input
          type="file"
          accept=".zip"
          onChange={handleFileChange}
          style={{ display: "none" }}
          id="file-upload"
          ref={fileInputRef}
        />
      </Paper>

      <Divider sx={{ my: { xs: 2, sm: 3 } }}>OR</Divider>

      {/* API URL Section */}
      <Box sx={{ mb: { xs: 2, sm: 3 } }}>
        <TextField
          label={isFocused || gitUrl ? "Repository URL" : ""}
          placeholder={!isFocused ? "Repository URL" : ""}
          variant="outlined"
          fullWidth
          value={gitUrl}
          onChange={handleApiUrlChange}
          onFocus={() => setIsFocused(true)}
          onBlur={() => !gitUrl && setIsFocused(false)}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "3px",
              border: "1px solid #ccc",
              backgroundColor: "#fff",
              px: 1,
            },
            "& .MuiInputBase-root": {
              display: "flex",
              alignItems: "center",
            },
            "& .MuiInputLabel-root": {
              color: "#6b7280",
            },
            "& .MuiOutlinedInput-notchedOutline": {
              border: "1px solid #ccc !important",
            },
            "& input": {
              padding: { xs: "14px", sm: "18px" },
              fontSize: { xs: "14px", sm: "16px" },
            },
            "& .MuiInputLabel-shrink": {
              transform: "translate(12px, -6px) scale(0.75) !important",
              background: "#fff",
              padding: "0 4px",
            },
          }}
        />
        {gitUrlError && (
          <Typography
            color="error"
            sx={{
              mt: { xs: 1 },
              mb: { xs: 1.5, sm: 2 },
              fontSize: { xs: "12px", sm: "15px" },
            }}
          >
            {gitUrlError.message}
          </Typography>
        )}
      </Box>
    </Box>
  );

  return (
    <>
      <CommonAccordion
        summary={"SAST Phase"}
        details={Details}
        type={"output"}
        buttons={[
          {
            label: "Trigger SAST SCAN",
            variant: "contained",
            startIcon: <GenerateButton />,
            sx: { float: "right", textTransform: "none" },
            disabled:
              (!file && !gitUrl.trim()) || !selectRunType || gitUrlError.error,
            onClick: handleSASTScanClick,
          },
        ]}
        defaultExpanded={
          Array.isArray(result) && result.length > 0 ? false : true
        }
        // fullScreenMode={true}
      />
      {openErrorPopup && (
        <MessagePopup
          modalOpen={openErrorPopup}
          okayHandler={() => setOpenErrorPopup(false)}
          message={errorMessage}
        />
      )}
      {sessionExpired && <SessionExpired />}
    </>
  );
};

export default ConstructionPhaseComponent;
